/**
 * "Previously quoted to {customer}" (prototype `vQuote`'s `histCard`).
 *
 * Pricing context: what this customer last paid, from whom, and in what pack size. A
 * purchaser about to quote AED 24 for a filter wants to know they bought it at 23.50 in June.
 *
 * `unavailable` — A8 (docs/bosun-required-api-round-2.md): no live endpoint exists for this
 * panel yet (see `lib/api/liveAvailability.ts`). Shown as an explicit "not available yet" line,
 * distinct from "no prior quotes recorded" — the latter is a fact about the customer, the former
 * is a fact about this backend, and conflating them would misrepresent genuine repeat business
 * as none.
 */

import { Badge } from '../../components/Badge'
import { Card, Placeholder } from '../../components/ui'
import { Money } from '../../components/Money'
import { formatDate } from '../../lib/format'
import { NOT_AVAILABLE_YET } from '../../lib/api/liveAvailability'
import type { QuotedItem } from '../../lib/types'

export function CustomerHistoryCard({
  customer,
  items,
  isPending,
  unavailable = false,
}: {
  customer: string
  items: QuotedItem[] | undefined
  isPending: boolean
  unavailable?: boolean
}) {
  return (
    <Card style={{ marginTop: 14 }}>
      <b>🕘 Previously quoted to {customer || 'this customer'} — repeat items &amp; conversion history</b>

      {unavailable ? (
        <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 6 }}>{NOT_AVAILABLE_YET}</div>
      ) : isPending ? (
        <div style={{ marginTop: 8 }}>
          <Placeholder>Loading history…</Placeholder>
        </div>
      ) : !items || items.length === 0 ? (
        <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 6 }}>
          No prior quotes recorded for this customer.
        </div>
      ) : (
        <table style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Item</th>
              <th>Supplier</th>
              <th className="right">Last price</th>
              <th>UOM conversions</th>
              <th>Ref</th>
              <th>Remark</th>
            </tr>
          </thead>
          <tbody>
            {items.map((entry) => (
              <tr key={`${entry.item_code}-${entry.ref}`}>
                <td>{entry.name}</td>
                <td>{entry.supplier}</td>
                <td className="right mono">
                  <Money value={entry.last_price} />
                </td>
                <td style={{ color: 'var(--muted)', fontSize: 11.5 }}>
                  {entry.uom_conversions
                    .map((c) => (c.factor === 1 ? `${c.uom} (base)` : `${c.uom}=${c.factor}`))
                    .join(', ')}
                </td>
                <td>
                  <Badge tone={entry.type === 'PO' ? 'ok' : 'info'}>
                    {entry.type} {entry.ref}
                  </Badge>{' '}
                  <span style={{ color: 'var(--muted)', fontSize: 11.5 }}>{formatDate(entry.date)}</span>
                </td>
                <td style={{ color: 'var(--muted)', fontSize: 11.5 }}>{entry.remark || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  )
}
